import React, { useEffect, useState, useCallback } from 'react';
import apiService from '../../../services/apiService';
import LeadsKanban from './LeadsKanban';
import ActivityTimeline from './ActivityTimeline';
import CarSelector from './CarSelector';
import { renderCustomerNameWithTravels, parseCustomerNameParts } from '../../../utils/nameFormatter';

interface LeadsManagementProps {
  userRole: string;
}

const STATUS_OPTIONS = [
  'New Lead',
  'Interested',
  'Hot',
  'Cold',
  'Call not received / Ringing',
  'On Hold',
  'Not Interested',
  'Virus'
];

const getStatusSelectStyle = (status: string) => {
  switch (status) {
    case 'Hot':
      return { background: '#FEE2E2', color: '#B91C1C', borderColor: '#FCA5A5', fontWeight: 700 };
    case 'Interested':
      return { background: '#D1FAE5', color: '#065F46', borderColor: '#6EE7B7', fontWeight: 700 };
    case 'Cold':
      return { background: '#E0F2FE', color: '#0369A1', borderColor: '#7DD3FC', fontWeight: 600 };
    case 'Call not received / Ringing':
      return { background: '#FEF3C7', color: '#92400E', borderColor: '#FCD34D', fontWeight: 700 };
    case 'On Hold':
      return { background: '#FFEDD5', color: '#C2410C', borderColor: '#FDBA74', fontWeight: 600 };
    case 'Not Interested':
      return { background: '#F1F5F9', color: '#64748B', borderColor: '#CBD5E1', fontWeight: 500 };
    case 'Virus':
      return { background: '#F3E8FF', color: '#7E22CE', borderColor: '#D8B4FE', fontWeight: 700 };
    case 'New Lead':
    default:
      return { background: '#EFF6FF', color: '#1D4ED8', borderColor: '#93C5FD', fontWeight: 600 };
  }
};

// Extract employee human remark from notes (excluding automated Facebook/Webhook system IDs)
const getEmployeeRemark = (note?: string | null): string => {
  if (!note) return '';
  return note
    .replace(/\[Leadgen ID:\s*\d+\]/gi, '')
    .replace(/Leadgen ID:\s*\d+(\s*\|\s*Email:[^|]+)?(\s*\|\s*Form( ID)?:[^|]+)?/gi, '')
    .replace(/Lead captured via[^|.]*/gi, '')
    .trim();
};

// Relative time helper
const timeAgo = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const formatDateTime = (dateStr: string): string =>
  new Date(dateStr).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const formatTime = (dateStr: string): string =>
  new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

const formatFollowUpDateTime = (dateStr: string) => {
  const d = new Date(dateStr);
  const datePart = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const timePart = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  return { datePart, timePart };
};

// Toast notification
const Toast: React.FC<{ message: string; onClose: () => void }> = ({ message, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{
      position: 'fixed', top: 20, right: 20, zIndex: 2000,
      background: '#1E293B', color: 'white', padding: '10px 16px',
      borderRadius: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', fontSize: '13px',
      animation: 'crm-slide-in 0.2s ease', maxWidth: '300px', display: 'flex', gap: 8, alignItems: 'center',
      borderLeft: '3px solid var(--crm-primary)'
    }}>
      <span>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: 16, marginLeft: 'auto', lineHeight: 1 }}>×</button>
    </div>
  );
};

// Local session memory cache for instant tab loads (SWR) - strictly scoped by userId
let cachedUserId: string | null = null;
let leadsCache: any[] | null = null;
let salesUsersCache: any[] | null = null;
let settingsCache: any | null = null;

const LeadsManagement: React.FC<LeadsManagementProps> = ({ userRole }) => {
  const userStr = sessionStorage.getItem('aswCrmUser') || localStorage.getItem('aswCrmUser') || sessionStorage.getItem('aswUser') || localStorage.getItem('aswUser');
  const currentUser = userStr ? JSON.parse(userStr) : null;
  const userId = currentUser?.id;
  const effectiveRole = userRole || currentUser?.role || 'Admin';

  const [leads, setLeads] = useState<any[]>(() => {
    if (cachedUserId === userId && leadsCache) return leadsCache;
    return [];
  });
  const [totalLeadsCount, setTotalLeadsCount] = useState(0);
  const [salesUsers, setSalesUsers] = useState<any[]>(() => {
    if (cachedUserId === userId && salesUsersCache) return salesUsersCache;
    return [];
  });
  const [loading, setLoading] = useState(() => !(cachedUserId === userId && leadsCache));
  const [showAddForm, setShowAddForm] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sortBy, setSortBy] = useState<'created_at' | 'follow_up_date' | 'status'>('created_at');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [overrideLeadId, setOverrideLeadId] = useState<string | null>(null);
  const [editingNameLead, setEditingNameLead] = useState<{ id: string; mainName: string; travelsName: string } | null>(null);
  const [leadSubTab, setLeadSubTab] = useState<'all' | 'active' | 'on-hold' | 'cancelled'>('all');
  const [allTabCount, setAllTabCount] = useState<number>(0);
  const [activeTabCount, setActiveTabCount] = useState<number>(0);
  const [onHoldTabCount, setOnHoldTabCount] = useState<number>(0);
  const [cancelledTabCount, setCancelledTabCount] = useState<number>(0);

  const isAdmin = effectiveRole === 'Admin';
  const [branches, setBranches] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [searchVal, setSearchVal] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCampaign, setFilterCampaign] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterFollowUp, setFilterFollowUp] = useState<string>('none');
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [distributionSettings, setDistributionSettings] = useState<any>(settingsCache || {
    auto_distribution_enabled: false,
    max_workload_limit: 10,
    distribution_mode: 'round-robin',
    vip_lead_senior_only: false,
    hot_lead_senior_only: false,
    vip_workflow_enabled: false
  });

  const [newLead, setNewLead] = useState<any>({
    customer_name: '',
    phone: '',
    source: '',
    campaign: '',
    country: '',
    city: '',
    industry: '',
    tags: '',
    follow_up_date: '',
    notes: '',
    branch_id: '',
    is_vip: false,
    interested_car: ''
  });

  const openEditNameModal = (lead: any) => {
    const parts = parseCustomerNameParts(lead.customer_name);
    setEditingNameLead({
      id: lead.id,
      mainName: parts.mainName,
      travelsName: parts.travelsName
    });
  };

  const fetchInitialData = useCallback(async (silent = false) => {
    if (!silent && !leadsCache) setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
        sort_by: sortBy,
        sort_dir: sortDir
      };
      if (search) params.search = search;
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      } else if (leadSubTab === 'active') {
        params.status = 'active';
      } else if (leadSubTab === 'on-hold') {
        params.status = 'On Hold';
      } else if (leadSubTab === 'cancelled') {
        params.status = 'Booking Cancelled';
      }

      if (filterCampaign !== 'all') params.campaign = filterCampaign;
      if (filterBranch !== 'all') params.branch_id = filterBranch;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterCountry) params.country = filterCountry;
      if (filterCity) params.city = filterCity;
      if (filterIndustry) params.industry = filterIndustry;
      if (filterFollowUp && filterFollowUp !== 'none') {
        params.follow_up_filter = filterFollowUp;
      }

      const [leadsRes, usersRes, branchesRes, settingsRes] = await Promise.all([
        apiService.getCrmLeads(params).catch(e => ({ success: false, error: e })),
        isAdmin ? apiService.getCrmUsers().catch(() => ({ success: true, data: [] })) : Promise.resolve({ success: true, data: [] }),
        apiService.getCrmBranches ? apiService.getCrmBranches().catch(() => ({ success: true, data: [] })) : Promise.resolve({ success: true, data: [] }),
        isAdmin && apiService.getLeadDistributionSettings ? apiService.getLeadDistributionSettings().catch(() => ({ success: false, data: null })) : Promise.resolve({ success: false, data: null })
      ]);

      if (leadsRes.success) {
        setLeads(leadsRes.data || []);
        const total = leadsRes.total !== undefined ? leadsRes.total : (leadsRes.count !== undefined ? leadsRes.count : (leadsRes.data || []).length);
        setTotalLeadsCount(total);
        cachedUserId = userId;
        leadsCache = leadsRes.data || [];
        if (leadsRes.tab_counts) {
          setAllTabCount(leadsRes.tab_counts.all || 0);
          setActiveTabCount(leadsRes.tab_counts.active || 0);
          setOnHoldTabCount(leadsRes.tab_counts.on_hold || 0);
          setCancelledTabCount(leadsRes.tab_counts.cancelled || 0);
        }
      }
      if (usersRes.success) {
        // Filter strictly for active Sales Team members / Sales Department
        const onlySalesUsers = (usersRes.data || []).filter((u: any) => {
          if (!u || u.status === 'Inactive' || u.status === 'Deactivated') return false;
          const dept = (u.department || '').trim().toLowerCase();
          const role = (u.role || '').trim().toLowerCase();
          return dept === 'sales' || role === 'sales team' || role.includes('sales');
        });
        setSalesUsers(onlySalesUsers);
        salesUsersCache = onlySalesUsers;
      }
      if (branchesRes.success) {
        setBranches(branchesRes.data || []);
      }
      if (settingsRes?.success && settingsRes.data) {
        setDistributionSettings(settingsRes.data);
        settingsCache = settingsRes.data;
      }
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortBy, sortDir, search, filterStatus, filterCampaign, filterBranch, filterDateFrom, filterDateTo, filterCountry, filterCity, filterIndustry, filterFollowUp, leadSubTab, isAdmin]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Clear memory cache if user changes (switching accounts)
  useEffect(() => {
    if (cachedUserId && cachedUserId !== userId) {
      cachedUserId = null;
      leadsCache = null;
      salesUsersCache = null;
      setLeads([]);
      setTotalLeadsCount(0);
      setAllTabCount(0);
    }
  }, [userId]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(searchVal);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchVal]);

  const handleStatusChange = async (leadOrId: any, newStatus: string) => {
    const lead = typeof leadOrId === 'object' ? leadOrId : leads.find(l => l.id === leadOrId);
    if (!lead) return;

    // Statuses that require follow-up scheduling or note capture
    const promptStatuses = [
      'Call not received / Ringing',
      'Interested',
      'Hot',
      'Cold',
      'On Hold',
      'Not Interested',
      'Virus'
    ];

    if (promptStatuses.includes(newStatus)) {
      openFollowUpModal(lead, newStatus);
      return;
    }

    setLeads(prev => {
      const updated = prev.map(l => l.id === lead.id ? { ...l, status: newStatus } : l);
      leadsCache = updated;
      return updated;
    });
    setToast(`Status updated to ${newStatus}`);

    try {
      await apiService.updateCrmLead(lead.id, { status: newStatus, performed_by: userId });
      fetchInitialData(true);
    } catch (err) {
      console.error('Error updating status:', err);
      fetchInitialData(true);
    }
  };

  const handleMarkContacted = async (leadId: string) => {
    const now = new Date().toISOString();
    setLeads(prev => {
      const updated = prev.map(l => l.id === leadId ? { ...l, first_contacted_at: now, status: l.status === 'New Lead' ? 'Contacted' : l.status } : l);
      leadsCache = updated;
      return updated;
    });
    setToast('Lead marked as Contacted');

    try {
      await apiService.updateCrmLead(leadId, {
        first_contacted_at: now,
        status: 'Contacted',
        performed_by: userId
      });
      fetchInitialData(true);
    } catch (err) {
      console.error('Error marking contacted:', err);
      fetchInitialData(true);
    }
  };

  const handleAddLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLead.customer_name || !newLead.phone) return;
    setSaving(true);
    try {
      const payload: any = {
        customer_name: newLead.customer_name,
        phone: newLead.phone,
        source: newLead.source || 'Manual',
        campaign: newLead.campaign || undefined,
        country: newLead.country || undefined,
        city: newLead.city || undefined,
        industry: newLead.industry || undefined,
        tags: newLead.tags ? newLead.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : [],
        follow_up_date: newLead.follow_up_date ? new Date(newLead.follow_up_date).toISOString() : undefined,
        notes: newLead.notes || undefined,
        branch_id: newLead.branch_id || undefined,
        is_vip: newLead.is_vip || false,
        interested_car: newLead.interested_car || undefined,
        performed_by: userId
      };
      const res = await apiService.createCrmLead(payload);
      if (res.success) {
        setToast('New lead created successfully');
        setShowAddForm(false);
        setNewLead({
          customer_name: '', phone: '', source: '', campaign: '', country: '', city: '',
          industry: '', tags: '', follow_up_date: '', notes: '', branch_id: '', is_vip: false, interested_car: ''
        });
        fetchInitialData();
      } else {
        alert(res.message || 'Failed to create lead');
      }
    } catch (err: any) {
      alert(err.message || 'Error creating lead');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await apiService.updateLeadDistributionSettings(distributionSettings);
      if (res.success) {
        setToast('Distribution settings saved');
        setShowSettingsPanel(false);
      } else {
        alert('Failed to save distribution settings');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Error saving distribution settings');
    }
  };

  // Follow-up scheduling modal state
  const [schedulingLead, setSchedulingLead] = useState<any | null>(null);
  const [schedulingTargetStatus, setSchedulingTargetStatus] = useState<string>('');
  const [followUpDateInput, setFollowUpDateInput] = useState<string>('');
  const [followUpNoteInput, setFollowUpNoteInput] = useState<string>('');
  const [selectedPreset, setSelectedPreset] = useState<string>('parso_morning');

  const openFollowUpModal = (lead: any, targetStatus?: string) => {
    setSchedulingLead(lead);
    const effectiveStatus = targetStatus || lead.status || 'New Lead';
    setSchedulingTargetStatus(effectiveStatus);
    setFollowUpNoteInput(getEmployeeRemark(lead.notes));

    const pad = (n: number) => n < 10 ? '0' + n : n;

    if (effectiveStatus === 'Call not received / Ringing') {
      const d = new Date();
      d.setHours(d.getHours() + 2);
      const localIso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      setFollowUpDateInput(localIso);
      setSelectedPreset('in_2_hours');
    } else if (effectiveStatus === 'Interested' || effectiveStatus === 'Hot') {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(10, 0, 0, 0);
      const localIso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T10:00`;
      setFollowUpDateInput(localIso);
      setSelectedPreset('tomorrow_morning');
    } else if (effectiveStatus === 'Not Interested' || effectiveStatus === 'Virus') {
      setFollowUpDateInput('');
      setSelectedPreset('none');
    } else if (lead.follow_up_date) {
      const d = new Date(lead.follow_up_date);
      const localIso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      setFollowUpDateInput(localIso);
      setSelectedPreset('custom');
    } else {
      const d = new Date();
      d.setDate(d.getDate() + 2);
      d.setHours(9, 0, 0, 0);
      const localIso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
      setFollowUpDateInput(localIso);
      setSelectedPreset('parso_morning');
    }
  };

  const setPresetIn2Hours = () => {
    const d = new Date();
    d.setHours(d.getHours() + 2);
    const pad = (n: number) => n < 10 ? '0' + n : n;
    setFollowUpDateInput(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    setSelectedPreset('in_2_hours');
  };

  const setPresetTodayEvening = () => {
    const d = new Date();
    d.setHours(17, 0, 0, 0);
    const pad = (n: number) => n < 10 ? '0' + n : n;
    setFollowUpDateInput(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T17:00`);
    setSelectedPreset('today_evening');
  };

  const setPresetTomorrowMorning = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    const pad = (n: number) => n < 10 ? '0' + n : n;
    setFollowUpDateInput(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T10:00`);
    setSelectedPreset('tomorrow_morning');
  };

  const setPresetDayAfterMorning = () => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    d.setHours(9, 0, 0, 0);
    const pad = (n: number) => n < 10 ? '0' + n : n;
    setFollowUpDateInput(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`);
    setSelectedPreset('parso_morning');
  };

  const setPresetNextWeek = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = day === 0 ? 1 : 8 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(10, 0, 0, 0);
    const pad = (n: number) => n < 10 ? '0' + n : n;
    setFollowUpDateInput(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T10:00`);
    setSelectedPreset('next_week');
  };

  const handleSaveFollowUp = async (e?: React.FormEvent, skipDate = false) => {
    if (e) e.preventDefault();
    if (!schedulingLead) return;
    
    if (!skipDate && !followUpDateInput && schedulingTargetStatus !== 'Not Interested' && schedulingTargetStatus !== 'Virus') {
      alert('Please select a date and time for follow-up or click "Save Without Schedule".');
      return;
    }

    const isoDate = (!skipDate && followUpDateInput) ? new Date(followUpDateInput).toISOString() : null;
    const leadId = schedulingLead.id;
    const note = followUpNoteInput.trim();
    const finalStatus = schedulingTargetStatus || schedulingLead.status;

    // Preserve leadgen ID if present in existing notes to protect FB deduplication
    const existingLeadgenMatch = schedulingLead.notes?.match(/Leadgen ID:\s*\d+/i)?.[0];
    const notesToSave = note 
      ? (existingLeadgenMatch && !note.includes(existingLeadgenMatch) ? `${note} [${existingLeadgenMatch}]` : note)
      : (existingLeadgenMatch || '');

    // Optimistic update
    setLeads(prev => {
      const updated = prev.map(l => l.id === leadId ? {
        ...l,
        status: finalStatus,
        follow_up_date: isoDate,
        notes: notesToSave
      } : l);
      leadsCache = updated;
      return updated;
    });

    setSchedulingLead(null);
    setToast(`Status updated to "${finalStatus}"${isoDate ? ' & follow-up scheduled' : ''}`);

    try {
      const updatePayload: any = {
        status: finalStatus,
        follow_up_date: isoDate,
        performed_by: userId,
        notes: notesToSave
      };

      await apiService.updateCrmLead(leadId, updatePayload);

      const formattedDate = isoDate 
        ? new Date(isoDate).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) 
        : 'No schedule';
      const logDesc = note 
        ? `Status: ${finalStatus} | Follow-up: ${formattedDate} | Remark: ${note}`
        : `Status changed to ${finalStatus}${isoDate ? ` | Follow-up scheduled for ${formattedDate}` : ''}`;

      await apiService.logCrmActivity({
        action_type: 'follow_up',
        entity_type: 'lead',
        entity_id: leadId,
        description: logDesc,
        performed_by: userId
      });

      fetchInitialData(true);
    } catch (err) {
      console.error('Error saving follow-up:', err);
      fetchInitialData(true);
    }
  };

  const handleClearFollowUp = async () => {
    if (!schedulingLead) return;
    const leadId = schedulingLead.id;
    setLeads(prev => {
      const updated = prev.map(l => l.id === leadId ? { ...l, follow_up_date: null } : l);
      leadsCache = updated;
      return updated;
    });
    setSchedulingLead(null);
    setToast('Follow-up cleared');

    try {
      await apiService.updateCrmLead(leadId, {
        follow_up_date: null,
        performed_by: userId
      });
      fetchInitialData(true);
    } catch (err) {
      console.error('Error clearing follow-up:', err);
      fetchInitialData(true);
    }
  };

  const handleSaveLeadName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingNameLead) return;
    const { id, mainName, travelsName } = editingNameLead;
    const formattedFullName = travelsName.trim()
      ? `${mainName.trim()} (${travelsName.trim()})`
      : mainName.trim();

    setSaving(true);
    try {
      setLeads(prev => {
        const updated = prev.map(l => l.id === id ? { ...l, customer_name: formattedFullName } : l);
        leadsCache = updated;
        return updated;
      });
      setEditingNameLead(null);
      setToast('Customer name updated');

      await apiService.updateCrmLead(id, {
        customer_name: formattedFullName,
        performed_by: userId
      });
      fetchInitialData(true);
    } catch (err) {
      console.error('Error updating customer name:', err);
      alert('Failed to update name');
      fetchInitialData(true);
    } finally {
      setSaving(false);
    }
  };

  const handleReassign = async (leadId: string, assignedTo: string) => {
    const selectedUser = salesUsers.find(u => u.id === assignedTo);
    
    // Optimistic local state update
    setLeads(prevLeads => {
      const updated = prevLeads.map(lead =>
        lead.id === leadId ? { ...lead, assigned_to: selectedUser ? { id: selectedUser.id, name: selectedUser.name } : null } : lead
      );
      leadsCache = updated;
      return updated;
    });

    try {
      const res = await apiService.updateCrmLead(leadId, { assigned_to: assignedTo, performed_by: userId });
      if (res.success) {
        if (selectedUser) setToast(`Lead reassigned to ${selectedUser.name}`);
        else setToast('Lead set to unassigned');
        fetchInitialData(true); // Silent background refresh
      } else {
        fetchInitialData(true);
        alert('Failed to reassign lead');
      }
    } catch {
      fetchInitialData(true);
      alert('Failed to reassign lead');
    }
  };

  const handleConvertLead = async (lead: any) => {
    if (!window.confirm(`Mark "${lead.customer_name}" as Booked and start Customer Workflow?`)) return;
    try {
      await apiService.updateCrmLead(lead.id, { status: 'Booked', performed_by: userId });
      const res = await apiService.createCrmCustomer({ lead_id: lead.id, customer_name: lead.customer_name, phone: lead.phone, car_details: lead.interested_car || '', performed_by: userId });
      if (res.success) {
        setToast(`Lead "${lead.customer_name}" marked as Booked and moved to Customer Workflow`);
      }
      fetchInitialData();
    } catch (err: any) {
      console.error(err);
      alert('Failed to convert lead: ' + (err.message || 'Error occurred'));
      fetchInitialData();
    }
  };

  const handleDeleteLead = async (leadId: string, name: string) => {
    if (!window.confirm(`Delete lead "${name}"? This cannot be undone.`)) return;
    try {
      const res = await apiService.deleteCrmLead(leadId, userId);
      if (res.success) { setToast(`Lead "${name}" deleted`); fetchInitialData(); }
      else alert(res.message || 'Failed to delete lead');
    } catch (err) { console.error(err); alert('Error deleting lead'); }
  };

  const handleSyncFacebook = async () => {
    // Emergency manual sync for admins — normally this runs automatically every 30 seconds in the backend
    setSyncing(true);
    try {
      const res = await apiService.syncCrmFacebookLeads();
      if (res.success) {
        setToast(`Force sync complete: ${res.details?.new_leads_imported ?? 0} new lead(s) imported`);
        fetchInitialData(true);
      } else {
        alert(res.message || 'Sync failed');
      }
    } catch (err: any) { alert(err.message || 'Error syncing Facebook leads'); }
    finally { setSyncing(false); }
  };

  const handleSort = (col: typeof sortBy) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const isFollowUpOverdue = (lead: any) => {
    if (!lead.follow_up_date || lead.status === 'Booking Cancelled' || lead.status === 'Converted' || lead.status === 'Booked' || lead.status === 'Not Interested' || lead.status === 'Virus') return false;
    return new Date(lead.follow_up_date).getTime() < Date.now();
  };

  const isFollowUpToday = (lead: any) => {
    if (!lead.follow_up_date || lead.status === 'Booking Cancelled' || lead.status === 'Converted' || lead.status === 'Booked' || lead.status === 'Not Interested' || lead.status === 'Virus') return false;
    const d = new Date(lead.follow_up_date);
    const today = new Date();
    return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
  };

  const isFollowUpTomorrow = (lead: any) => {
    if (!lead.follow_up_date || lead.status === 'Booking Cancelled' || lead.status === 'Converted' || lead.status === 'Booked' || lead.status === 'Not Interested' || lead.status === 'Virus') return false;
    const d = new Date(lead.follow_up_date);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return d.getFullYear() === tomorrow.getFullYear() && d.getMonth() === tomorrow.getMonth() && d.getDate() === tomorrow.getDate();
  };

  const isFollowUpUpcoming = (lead: any) => {
    if (!lead.follow_up_date || lead.status === 'Booking Cancelled' || lead.status === 'Converted' || lead.status === 'Booked' || lead.status === 'Not Interested' || lead.status === 'Virus') return false;
    const d = new Date(lead.follow_up_date);
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    return d.getTime() > endOfDay.getTime();
  };

  const uniqueCampaigns = Array.from(new Set(leads.map(l => l.campaign).filter(Boolean)));

  const displayLeads = leads;

  const sortedLeads = displayLeads;
  const paginatedLeads = displayLeads;

  const totalPages = Math.ceil(totalLeadsCount / itemsPerPage);
  const startIndex = totalLeadsCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalLeadsCount);

  const totalFollowUpsCount = leads.filter(l => Boolean(l.follow_up_date) && l.status !== 'Booking Cancelled' && l.status !== 'Converted' && l.status !== 'Booked').length;
  const overdueCount = leads.filter(isFollowUpOverdue).length;
  const dueTodayCount = leads.filter(isFollowUpToday).length;
  const tomorrowCount = leads.filter(isFollowUpTomorrow).length;
  const upcomingCount = leads.filter(isFollowUpUpcoming).length;
  const newLeadsCount = leads.filter(l => l.status === 'New Lead').length;

  const SortTh: React.FC<{ col: typeof sortBy; label: string }> = ({ col, label }) => (
    <th
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => handleSort(col)}
    >
      {label}
      {sortBy === col && <span style={{ marginLeft: 4, opacity: 0.6, fontSize: 10 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  const clearAllFilters = () => {
    setSearch(''); setSearchVal(''); setFilterStatus('all'); setFilterCampaign('all'); setFilterBranch('all');
    setFilterCountry(''); setFilterCity(''); setFilterIndustry('');
    setFilterDateFrom(''); setFilterDateTo(''); setFilterFollowUp('none');
    setLeadSubTab('all');
    setCurrentPage(1);
  };

  const hasActiveFilters = search || filterStatus !== 'all' || filterCampaign !== 'all' || filterBranch !== 'all' || filterCountry || filterCity || filterIndustry || filterDateFrom || filterDateTo || (filterFollowUp !== 'none') || leadSubTab !== 'all';

  if (loading) return <div className="crm-loading"><div className="crm-spinner"></div> Loading leads...</div>;

  return (
    <div>
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
      {selectedLead && <ActivityTimeline lead={selectedLead} onClose={() => setSelectedLead(null)} />}

      {/* ── Header ── */}
      <div className="crm-page-header">
        <div className="crm-page-header-left">
          <h2>{isAdmin ? 'All Leads' : 'My Leads'}</h2>
          <p style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', margin: '4px 0 0' }}>
            <span>Total Leads: <strong style={{ color: 'var(--crm-primary)', fontSize: '14px' }}>{totalLeadsCount}</strong></span>
            {newLeadsCount > 0 && <span style={{ padding: '1px 7px', background: '#DBEAFE', color: '#1D4ED8', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>{newLeadsCount} NEW</span>}
            {totalFollowUpsCount > 0 && (
              <span
                onClick={() => setFilterFollowUp(v => v === 'all_followups' ? 'none' : 'all_followups')}
                style={{
                  padding: '2px 8px',
                  background: filterFollowUp === 'all_followups' ? '#DBEAFE' : '#EFF6FF',
                  color: '#1E40AF',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  border: filterFollowUp === 'all_followups' ? '1.5px solid #2563EB' : '1px solid #BFDBFE'
                }}
                title="Click to filter all scheduled follow-ups"
              >
                📅 {totalFollowUpsCount} FOLLOW-UPS
              </span>
            )}
            {dueTodayCount > 0 && (
              <span
                onClick={() => setFilterFollowUp(v => v === 'today' ? 'none' : 'today')}
                style={{
                  padding: '2px 8px',
                  background: filterFollowUp === 'today' ? '#FEF3C7' : '#FFFBEB',
                  color: '#B45309',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  border: filterFollowUp === 'today' ? '1.5px solid #D97706' : '1px solid #FDE68A'
                }}
                title="Click to filter today's follow-ups"
              >
                🌅 {dueTodayCount} DUE TODAY
              </span>
            )}
            {tomorrowCount > 0 && (
              <span
                onClick={() => setFilterFollowUp(v => v === 'tomorrow' ? 'none' : 'tomorrow')}
                style={{
                  padding: '2px 8px',
                  background: filterFollowUp === 'tomorrow' ? '#E0F2FE' : '#F0F9FF',
                  color: '#0369A1',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  border: filterFollowUp === 'tomorrow' ? '1.5px solid #0284C7' : '1px solid #BAE6FD'
                }}
                title="Click to filter tomorrow's follow-ups"
              >
                ☀️ {tomorrowCount} TOMORROW
              </span>
            )}
            {overdueCount > 0 && (
              <span
                onClick={() => setFilterFollowUp(v => v === 'overdue' ? 'none' : 'overdue')}
                style={{
                  padding: '2px 8px',
                  background: filterFollowUp === 'overdue' ? '#FEE2E2' : '#FEF2F2',
                  color: '#B91C1C',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  border: filterFollowUp === 'overdue' ? '1.5px solid #DC2626' : '1px solid #FECACA'
                }}
                title="Click to filter overdue follow-ups"
              >
                ⚠️ {overdueCount} OVERDUE
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', background: 'var(--crm-bg)', borderRadius: 'var(--crm-radius-sm)', border: '1px solid var(--crm-border)', overflow: 'hidden' }}>
            <button
              style={{ padding: '5px 12px', fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: viewMode === 'table' ? 'var(--crm-primary)' : 'transparent', color: viewMode === 'table' ? 'white' : 'var(--crm-text-secondary)', transition: 'all 0.15s' }}
              onClick={() => setViewMode('table')}
            >Table</button>
            <button
              style={{ padding: '5px 12px', fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: viewMode === 'kanban' ? 'var(--crm-primary)' : 'transparent', color: viewMode === 'kanban' ? 'white' : 'var(--crm-text-secondary)', transition: 'all 0.15s' }}
              onClick={() => setViewMode('kanban')}
            >Kanban</button>
          </div>
          {userRole === 'Admin' && (
            <>
              {/* Auto-sync status badge — FB leads sync automatically every 30s in the backend */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
                background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 'var(--crm-radius-sm)',
                fontSize: 11.5, color: '#15803D', fontWeight: 600, whiteSpace: 'nowrap'
              }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22C55E', display: 'inline-block', boxShadow: '0 0 0 2px rgba(34,197,94,0.25)', flexShrink: 0 }} />
                FB Auto-Sync Active
              </div>
              <button className="crm-btn crm-btn-ghost" onClick={() => setShowSettingsPanel(v => !v)} style={{ fontSize: 12.5 }}>
                {showSettingsPanel ? 'Close Rules' : 'Distribution Rules'}
              </button>
            </>
          )}
          <button className="crm-btn crm-btn-ghost" onClick={() => { leadsCache = null; fetchInitialData(false); }} style={{ fontSize: 12.5 }}>Refresh</button>
          <button className="crm-btn crm-btn-success" onClick={() => setShowAddForm(v => !v)} style={{ fontSize: 12.5 }}>
            {showAddForm ? 'Cancel' : '+ New Lead'}
          </button>
        </div>
      </div>

      {/* ── Sub-Page Navigation Switcher Tabs ── */}
      <div className="crm-subpage-tab-bar" style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '14px',
        background: 'var(--crm-bg)',
        padding: '6px',
        borderRadius: 'var(--crm-radius-md)',
        border: '1.5px solid var(--crm-border)',
        boxShadow: 'var(--crm-shadow-sm)',
        flexWrap: 'wrap'
      }}>
        {/* Tab 0: All Leads */}
        <button
          type="button"
          onClick={() => { setLeadSubTab('all'); setFilterStatus('all'); setCurrentPage(1); }}
          style={{
            flex: '1 1 180px',
            padding: '10px 16px',
            fontSize: '13.5px',
            fontWeight: 700,
            border: leadSubTab === 'all' ? '1.5px solid var(--crm-primary)' : '1px solid transparent',
            borderRadius: 'var(--crm-radius-sm)',
            cursor: 'pointer',
            background: leadSubTab === 'all' ? 'white' : 'transparent',
            color: leadSubTab === 'all' ? 'var(--crm-primary)' : 'var(--crm-text-secondary)',
            boxShadow: leadSubTab === 'all' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.18s ease'
          }}
        >
          <span>📁 All Leads</span>
          <span style={{
            background: leadSubTab === 'all' ? 'var(--crm-primary-light)' : '#E2E8F0',
            color: leadSubTab === 'all' ? 'var(--crm-primary)' : '#475569',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '11.5px',
            fontWeight: 800
          }}>{allTabCount || totalLeadsCount}</span>
        </button>

        {/* Tab 1: Active Pipeline Leads */}
        <button
          type="button"
          onClick={() => { setLeadSubTab('active'); setFilterStatus('all'); setCurrentPage(1); }}
          style={{
            flex: '1 1 180px',
            padding: '10px 16px',
            fontSize: '13.5px',
            fontWeight: 700,
            border: leadSubTab === 'active' ? '1.5px solid var(--crm-primary)' : '1px solid transparent',
            borderRadius: 'var(--crm-radius-sm)',
            cursor: 'pointer',
            background: leadSubTab === 'active' ? 'white' : 'transparent',
            color: leadSubTab === 'active' ? 'var(--crm-primary)' : 'var(--crm-text-secondary)',
            boxShadow: leadSubTab === 'active' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.18s ease'
          }}
        >
          <span>📋 Active Pipeline</span>
          <span style={{
            background: leadSubTab === 'active' ? 'var(--crm-primary-light)' : '#E2E8F0',
            color: leadSubTab === 'active' ? 'var(--crm-primary)' : '#475569',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '11.5px',
            fontWeight: 800
          }}>{activeTabCount || (totalLeadsCount > (onHoldTabCount + cancelledTabCount) ? totalLeadsCount - onHoldTabCount - cancelledTabCount : totalLeadsCount)}</span>
        </button>

        {/* Tab 2: On Hold Leads Sub-Page */}
        <button
          type="button"
          onClick={() => { setLeadSubTab('on-hold'); setFilterStatus('all'); setCurrentPage(1); }}
          style={{
            flex: '1 1 180px',
            padding: '10px 16px',
            fontSize: '13.5px',
            fontWeight: 700,
            border: leadSubTab === 'on-hold' ? '1.5px solid #D97706' : '1px solid transparent',
            borderRadius: 'var(--crm-radius-sm)',
            cursor: 'pointer',
            background: leadSubTab === 'on-hold' ? '#FEF3C7' : 'transparent',
            color: leadSubTab === 'on-hold' ? '#92400E' : 'var(--crm-text-secondary)',
            boxShadow: leadSubTab === 'on-hold' ? '0 2px 8px rgba(217,119,6,0.12)' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.18s ease'
          }}
        >
          <span>⏸️ On-Hold</span>
          <span style={{
            background: '#F59E0B',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '11.5px',
            fontWeight: 800
          }}>{onHoldTabCount}</span>
        </button>

        {/* Tab 3: Cancelled Leads Sub-Page */}
        <button
          type="button"
          onClick={() => { setLeadSubTab('cancelled'); setFilterStatus('all'); setCurrentPage(1); }}
          style={{
            flex: '1 1 180px',
            padding: '10px 16px',
            fontSize: '13.5px',
            fontWeight: 700,
            border: leadSubTab === 'cancelled' ? '1.5px solid #DC2626' : '1px solid transparent',
            borderRadius: 'var(--crm-radius-sm)',
            cursor: 'pointer',
            background: leadSubTab === 'cancelled' ? '#FEE2E2' : 'transparent',
            color: leadSubTab === 'cancelled' ? '#991B1B' : 'var(--crm-text-secondary)',
            boxShadow: leadSubTab === 'cancelled' ? '0 2px 8px rgba(220,38,38,0.12)' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.18s ease'
          }}
        >
          <span>🛑 Cancelled</span>
          <span style={{
            background: '#EF4444',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '11.5px',
            fontWeight: 800
          }}>{cancelledTabCount}</span>
        </button>
      </div>

      {/* ── Distribution Settings Panel ── */}
      {userRole === 'Admin' && showSettingsPanel && (
        <form className="crm-form-panel" onSubmit={handleSaveSettings} style={{ borderLeft: '3px solid var(--crm-primary)', marginBottom: 12 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 12px', color: 'var(--crm-text-primary)' }}>Lead Distribution Settings</h3>
          <div className="crm-form-fields" style={{ gap: 8 }}>
            <div className="crm-form-field" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}>
              <input type="checkbox" id="auto_dist_enabled" checked={distributionSettings.auto_distribution_enabled} onChange={e => setDistributionSettings({ ...distributionSettings, auto_distribution_enabled: e.target.checked })} style={{ width: 'auto', margin: 0 }} />
              <label htmlFor="auto_dist_enabled" style={{ cursor: 'pointer', margin: 0, fontWeight: 600, fontSize: 13 }}>Enable Automatic Distribution</label>
            </div>
            <div className="crm-form-field">
              <label>Max Workload Limit</label>
              <input type="number" min="1" max="100" value={distributionSettings.max_workload_limit || 10} onChange={e => setDistributionSettings({ ...distributionSettings, max_workload_limit: parseInt(e.target.value) || 10 })} />
            </div>
            <div className="crm-form-field">
              <label>Allocation Mode</label>
              <select value={distributionSettings.distribution_mode || 'round-robin'} onChange={e => setDistributionSettings({ ...distributionSettings, distribution_mode: e.target.value })}>
                <option value="round-robin">Round Robin (Balanced History)</option>
                <option value="workload-based">Workload Capacity Balancing</option>
              </select>
            </div>
            <div className="crm-form-field" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}>
              <input type="checkbox" id="vip_senior" checked={distributionSettings.vip_lead_senior_only || false} onChange={e => setDistributionSettings({ ...distributionSettings, vip_lead_senior_only: e.target.checked })} style={{ width: 'auto', margin: 0 }} />
              <label htmlFor="vip_senior" style={{ cursor: 'pointer', margin: 0, fontWeight: 600, fontSize: 13 }}>Assign VIP Leads to Seniors Only</label>
            </div>
            <div className="crm-form-field" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}>
              <input type="checkbox" id="hot_senior" checked={distributionSettings.hot_lead_senior_only || false} onChange={e => setDistributionSettings({ ...distributionSettings, hot_lead_senior_only: e.target.checked })} style={{ width: 'auto', margin: 0 }} />
              <label htmlFor="hot_senior" style={{ cursor: 'pointer', margin: 0, fontWeight: 600, fontSize: 13 }}>Prioritize Seniors for Hot Leads</label>
            </div>
            <div className="crm-form-field" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}>
              <input type="checkbox" id="vip_workflow" checked={distributionSettings.vip_workflow_enabled || false} onChange={e => setDistributionSettings({ ...distributionSettings, vip_workflow_enabled: e.target.checked })} style={{ width: 'auto', margin: 0 }} />
              <label htmlFor="vip_workflow" style={{ cursor: 'pointer', margin: 0, fontWeight: 600, fontSize: 13 }}>Enable Dedicated VIP Routing</label>
            </div>
          </div>
          <div className="crm-form-actions" style={{ marginTop: 12, gap: 8 }}>
            <button type="submit" className="crm-btn crm-btn-success">Save Settings</button>
            <button
              type="button"
              className="crm-btn crm-btn-ghost"
              onClick={handleSyncFacebook}
              disabled={syncing}
              title="Force-sync Facebook leads now (normally happens automatically every 30 seconds)"
              style={{ fontSize: 12, opacity: 0.8 }}
            >
              {syncing ? 'Syncing...' : 'Force FB Sync'}
            </button>
            <button type="button" className="crm-btn crm-btn-ghost" onClick={() => setShowSettingsPanel(false)}>Cancel</button>
          </div>
        </form>
      )}

      {/* ── Search & Filters ── */}
      <div style={{ background: 'var(--crm-bg)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '10px 12px', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Search */}
          <div style={{ display: 'flex', alignItems: 'center', background: 'white', border: '1.5px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '0 10px', flex: '1 1 200px', minWidth: 160 }}>
            <svg width="13" height="13" viewBox="0 0 20 20" fill="none" style={{ color: 'var(--crm-text-muted)', flexShrink: 0, marginRight: 6 }}>
              <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8"/>
              <path d="M14 14l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search name, phone, campaign..."
              value={searchVal}
              onChange={e => setSearchVal(e.target.value)}
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, padding: '6px 0', background: 'transparent', color: 'var(--crm-text-primary)' }}
            />
            {searchVal && <button onClick={() => setSearchVal('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--crm-text-muted)', fontSize: 14, padding: 0 }}>×</button>}
          </div>

          {/* Status */}
          <select className="crm-status-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          {/* Campaign */}
          <select className="crm-status-select" value={filterCampaign} onChange={e => setFilterCampaign(e.target.value)} style={{ maxWidth: 150 }}>
            <option value="all">All Campaigns</option>
            {uniqueCampaigns.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          {/* Branch */}
          {isAdmin && (
            <select className="crm-status-select" value={filterBranch} onChange={e => setFilterBranch(e.target.value)}>
              <option value="all">All Branches</option>
              {branches.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}

          {/* Date range */}
          <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} title="From date"
            style={{ padding: '5px 8px', border: '1.5px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', fontSize: 12.5, color: 'var(--crm-text-primary)', background: 'white', cursor: 'pointer' }} />
          <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} title="To date"
            style={{ padding: '5px 8px', border: '1.5px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', fontSize: 12.5, color: 'var(--crm-text-primary)', background: 'white', cursor: 'pointer' }} />

          {/* Country / City / Industry */}
          <input type="text" placeholder="Country" value={filterCountry} onChange={e => setFilterCountry(e.target.value)}
            style={{ padding: '5px 8px', border: '1.5px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', fontSize: 12.5, width: 90, background: 'white' }} />
          <input type="text" placeholder="City" value={filterCity} onChange={e => setFilterCity(e.target.value)}
            style={{ padding: '5px 8px', border: '1.5px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', fontSize: 12.5, width: 80, background: 'white' }} />
          <input type="text" placeholder="Industry" value={filterIndustry} onChange={e => setFilterIndustry(e.target.value)}
            style={{ padding: '5px 8px', border: '1.5px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', fontSize: 12.5, width: 90, background: 'white' }} />

          {/* Follow-up Filter Selector */}
          <select
            className="crm-status-select"
            value={filterFollowUp}
            onChange={e => setFilterFollowUp(e.target.value)}
            style={{
              maxWidth: 210,
              fontWeight: filterFollowUp !== 'none' ? 700 : 500,
              color: filterFollowUp === 'due' || filterFollowUp === 'overdue' ? '#B91C1C' : filterFollowUp === 'all_followups' ? '#1D4ED8' : filterFollowUp === 'tomorrow' ? '#0369A1' : filterFollowUp === 'today' ? '#B45309' : 'var(--crm-text-primary)',
              background: filterFollowUp === 'due' || filterFollowUp === 'overdue' ? '#FEE2E2' : filterFollowUp === 'all_followups' ? '#EFF6FF' : filterFollowUp === 'tomorrow' ? '#E0F2FE' : filterFollowUp === 'today' ? '#FEF3C7' : 'white',
              borderColor: filterFollowUp !== 'none' ? 'var(--crm-primary)' : 'var(--crm-border)'
            }}
          >
            <option value="none">📋 All Leads (No Follow-up Filter)</option>
            <option value="all_followups">📅 All Scheduled Follow-ups ({totalFollowUpsCount})</option>
            <option value="due">⏰ Due Today & Overdue ({dueTodayCount + overdueCount})</option>
            <option value="tomorrow">☀️ Tomorrow's Follow-ups ({tomorrowCount})</option>
            <option value="today">🌅 Due Today ({dueTodayCount})</option>
            <option value="overdue">⚠️ Overdue ({overdueCount})</option>
            <option value="upcoming">🚀 Upcoming / Later ({upcomingCount})</option>
          </select>

          {/* Quick toggle for All Follow-ups */}
          <button
            type="button"
            onClick={() => setFilterFollowUp(v => v === 'all_followups' ? 'none' : 'all_followups')}
            style={{
              padding: '5px 12px', fontSize: 12.5, fontWeight: 700, border: '1.5px solid', borderRadius: 'var(--crm-radius-sm)',
              cursor: 'pointer', whiteSpace: 'nowrap',
              background: filterFollowUp === 'all_followups' ? '#DBEAFE' : 'white',
              color: filterFollowUp === 'all_followups' ? '#1D4ED8' : totalFollowUpsCount > 0 ? '#2563EB' : 'var(--crm-text-secondary)',
              borderColor: filterFollowUp === 'all_followups' ? '#3B82F6' : totalFollowUpsCount > 0 ? '#93C5FD' : 'var(--crm-border)',
              display: 'inline-flex', alignItems: 'center', gap: '5px', transition: 'all 0.15s'
            }}
            title="Show all leads that have any follow-up scheduled (Today, Tomorrow, Overdue, Future)"
          >
            <span>📅 All Follow-ups</span>
            {totalFollowUpsCount > 0 && (
              <span style={{
                background: filterFollowUp === 'all_followups' ? '#1D4ED8' : '#3B82F6',
                color: 'white',
                padding: '1px 6px',
                borderRadius: '10px',
                fontSize: '10.5px',
                fontWeight: 800
              }}>
                {totalFollowUpsCount}
              </span>
            )}
          </button>

          {/* Quick toggle for Due Follow-ups */}
          <button
            type="button"
            onClick={() => setFilterFollowUp(v => v === 'due' ? 'none' : 'due')}
            style={{
              padding: '5px 12px', fontSize: 12.5, fontWeight: 700, border: '1.5px solid', borderRadius: 'var(--crm-radius-sm)',
              cursor: 'pointer', whiteSpace: 'nowrap',
              background: filterFollowUp === 'due' ? '#FEE2E2' : 'white',
              color: filterFollowUp === 'due' ? '#B91C1C' : (overdueCount > 0 || dueTodayCount > 0) ? '#B45309' : 'var(--crm-text-secondary)',
              borderColor: filterFollowUp === 'due' ? '#EF4444' : (overdueCount > 0 || dueTodayCount > 0) ? '#F59E0B' : 'var(--crm-border)',
              display: 'inline-flex', alignItems: 'center', gap: '5px', transition: 'all 0.15s'
            }}
            title="Filter leads that are due today or overdue"
          >
            <span>⏰ Due Today / Overdue</span>
            {(overdueCount > 0 || dueTodayCount > 0) && (
              <span style={{
                background: filterFollowUp === 'due' ? '#DC2626' : '#D97706',
                color: 'white',
                padding: '1px 6px',
                borderRadius: '10px',
                fontSize: '10.5px',
                fontWeight: 800
              }}>
                {overdueCount + dueTodayCount}
              </span>
            )}
          </button>

          {/* Quick toggle for Aaj Ka Schedule */}
          <button
            type="button"
            onClick={() => setFilterFollowUp(v => v === 'today' ? 'none' : 'today')}
            style={{
              padding: '5px 12px', fontSize: 12.5, fontWeight: 700, border: '1.5px solid', borderRadius: 'var(--crm-radius-sm)',
              cursor: 'pointer', whiteSpace: 'nowrap',
              background: filterFollowUp === 'today' ? '#FEF3C7' : 'white',
              color: filterFollowUp === 'today' ? '#92400E' : dueTodayCount > 0 ? '#B45309' : 'var(--crm-text-secondary)',
              borderColor: filterFollowUp === 'today' ? '#D97706' : dueTodayCount > 0 ? '#F59E0B' : 'var(--crm-border)',
              display: 'inline-flex', alignItems: 'center', gap: '5px', transition: 'all 0.15s',
              boxShadow: dueTodayCount > 0 ? '0 1px 3px rgba(217,119,6,0.15)' : 'none'
            }}
            title="Filter leads scheduled for today"
          >
            <span>🔔 Aaj Ka Schedule</span>
            {dueTodayCount > 0 && (
              <span style={{
                background: filterFollowUp === 'today' ? '#B45309' : '#D97706',
                color: 'white',
                padding: '1px 6px',
                borderRadius: '10px',
                fontSize: '10.5px',
                fontWeight: 800
              }}>
                {dueTodayCount}
              </span>
            )}
          </button>

          {hasActiveFilters && (
            <button onClick={clearAllFilters}
              style={{ padding: '5px 10px', fontSize: 12, fontWeight: 600, border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', cursor: 'pointer', background: 'white', color: 'var(--crm-text-muted)', whiteSpace: 'nowrap' }}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* ── Add Lead Form ── */}
      {showAddForm && (
        <form className="crm-form-panel" onSubmit={handleAddLead} style={{ marginBottom: 12, borderLeft: '3px solid var(--crm-success)' }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 10px', color: 'var(--crm-text-primary)' }}>New Lead</h3>
          <div className="crm-form-fields">
            <div className="crm-form-field"><label>Customer Name *</label>
              <input type="text" placeholder="Full Name" required value={newLead.customer_name} onChange={e => setNewLead({ ...newLead, customer_name: e.target.value })} />
            </div>
            <div className="crm-form-field"><label>Phone *</label>
              <input type="text" placeholder="+91 98765…" required value={newLead.phone} onChange={e => setNewLead({ ...newLead, phone: e.target.value })} />
            </div>
            <div className="crm-form-field"><label>Source</label>
              <input type="text" placeholder="Website, Facebook…" value={newLead.source} onChange={e => setNewLead({ ...newLead, source: e.target.value })} />
            </div>
            <div className="crm-form-field"><label>Campaign</label>
              <input type="text" placeholder="e.g. Meta June Campaign" value={newLead.campaign} onChange={e => setNewLead({ ...newLead, campaign: e.target.value })} />
            </div>
            <div className="crm-form-field"><label>Country</label>
              <input type="text" placeholder="India" value={newLead.country} onChange={e => setNewLead({ ...newLead, country: e.target.value })} />
            </div>
            <div className="crm-form-field"><label>City</label>
              <input type="text" placeholder="Mumbai" value={newLead.city} onChange={e => setNewLead({ ...newLead, city: e.target.value })} />
            </div>
            <div className="crm-form-field"><label>Industry</label>
              <input type="text" placeholder="IT, Automotive" value={newLead.industry} onChange={e => setNewLead({ ...newLead, industry: e.target.value })} />
            </div>
            <div className="crm-form-field"><label>Tags (comma separated)</label>
              <input type="text" placeholder="referral, emi, fleet" value={newLead.tags} onChange={e => setNewLead({ ...newLead, tags: e.target.value })} />
            </div>
            <div className="crm-form-field"><label>Follow-up Date & Time</label>
              <input type="datetime-local" value={newLead.follow_up_date} onChange={e => setNewLead({ ...newLead, follow_up_date: e.target.value })} />
            </div>
            <div className="crm-form-field"><label>Notes</label>
              <input type="text" placeholder="Any notes…" value={newLead.notes} onChange={e => setNewLead({ ...newLead, notes: e.target.value })} />
            </div>
            {isAdmin && (
              <div className="crm-form-field">
                <label>Branch Office</label>
                <select value={newLead.branch_id || ''} onChange={e => setNewLead({ ...newLead, branch_id: e.target.value })}>
                  <option value="">Main Branch/Thane</option>
                  {branches.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
            <div className="crm-form-field" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18 }}>
              <input type="checkbox" id="is_vip_new" checked={newLead.is_vip} onChange={e => setNewLead({ ...newLead, is_vip: e.target.checked })} style={{ width: 'auto', margin: 0 }} />
              <label htmlFor="is_vip_new" style={{ cursor: 'pointer', margin: 0, fontWeight: 600, fontSize: 13 }}>Mark as VIP Lead</label>
            </div>
            <div className="crm-form-field" style={{ flex: '1 1 100%' }}>
              <CarSelector value={newLead.interested_car} onChange={(val: string) => setNewLead({ ...newLead, interested_car: val })} />
            </div>
          </div>
          <div className="crm-form-actions">
            <button type="submit" className="crm-btn crm-btn-success" disabled={saving}>{saving ? 'Saving...' : 'Save Lead'}</button>
            <button type="button" className="crm-btn crm-btn-ghost" onClick={() => setShowAddForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {/* ── Table / Kanban ── */}
      {sortedLeads.length === 0 ? (
        <div className="crm-empty-state">
          <h3>No leads found</h3>
          <p>Try adjusting your filters or click "+ New Lead" to create one.</p>
          {hasActiveFilters && (
            <button className="crm-btn crm-btn-ghost" style={{ marginTop: 10 }} onClick={clearAllFilters}>Clear all filters</button>
          )}
        </div>
      ) : viewMode === 'kanban' ? (
        <LeadsKanban leads={sortedLeads} onStatusChange={handleStatusChange} onLeadClick={setSelectedLead} isAdmin={isAdmin} branches={branches} />
      ) : (
        <div className="crm-table-wrapper">
          <table className="crm-table" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <SortTh col="created_at" label="Created" />
                <th style={{ minWidth: 180 }}>Lead Info</th>
                <th>Phone</th>
                <th>Source / Campaign</th>
                <th>Location</th>
                <th>Branch</th>
                <th>Car Interest</th>
                <th>Tags</th>
                <SortTh col="follow_up_date" label="Follow-up Date & Time" />
                <SortTh col="status" label="Status" />
                {isAdmin && <th>Assigned To</th>}
                <th style={{ minWidth: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLeads.map((lead: any) => {
                const overdue = isFollowUpOverdue(lead);
                const dueToday = isFollowUpToday(lead);
                return (
                  <tr
                    key={lead.id}
                    style={{
                      background: overdue ? '#FFF1F2' : dueToday ? '#FEF9C3' : '',
                      borderLeft: overdue ? '4px solid #EF4444' : dueToday ? '4px solid #D97706' : '4px solid transparent',
                      transition: 'background 0.15s ease'
                    }}
                  >

                    {/* Created date */}
                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                      <div style={{ fontSize: 12, color: 'var(--crm-text-primary)', fontWeight: 500 }}>
                        {formatDate(lead.created_at)}
                      </div>
                      <div style={{ fontSize: 10.5, color: 'var(--crm-text-muted)', marginTop: 1 }}>
                        {formatTime(lead.created_at)}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--crm-text-muted)', marginTop: 1, fontStyle: 'italic' }}>
                        {timeAgo(lead.created_at)}
                      </div>
                    </td>

                    {/* Lead info */}
                    <td style={{ verticalAlign: 'top' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
                        <span
                          style={{ fontWeight: 700, fontSize: 12.5, cursor: 'pointer', color: 'var(--crm-primary)' }}
                          onClick={() => setSelectedLead(lead)}
                          title="View Activity Timeline"
                        >
                          {renderCustomerNameWithTravels(lead.customer_name)}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openEditNameModal(lead); }}
                          style={{
                            background: '#F1F5F9',
                            border: '1px solid #CBD5E1',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            color: '#475569',
                            padding: '1px 5px',
                            fontWeight: 600,
                            lineHeight: 1
                          }}
                          title="Edit Naming / Tours & Travels Name"
                        >
                          ✏️ Edit
                        </button>
                        {lead.status === 'New Lead' && (
                          <span style={{ fontSize: 9, padding: '1px 5px', background: '#DBEAFE', color: '#1D4ED8', borderRadius: 3, fontWeight: 800, letterSpacing: 0.3 }}>NEW</span>
                        )}
                        {lead.is_vip && (
                          <span style={{ fontSize: 9, padding: '1px 5px', background: '#FEF3C7', color: '#D97706', borderRadius: 3, fontWeight: 800, letterSpacing: 0.3 }}>VIP</span>
                        )}
                      </div>
                      {(() => {
                        const remark = getEmployeeRemark(lead.notes);
                        if (!remark) return null;
                        return (
                          <div style={{
                            marginTop: '4px',
                            marginBottom: '4px',
                            padding: '4px 8px',
                            background: '#EFF6FF',
                            borderLeft: '3px solid #2563EB',
                            borderRadius: '4px',
                            fontSize: '11.5px',
                            color: '#0F172A',
                            lineHeight: '1.4',
                            maxWidth: '240px',
                            wordBreak: 'break-word',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                          }}>
                            <span style={{ fontWeight: 700, color: '#1D4ED8', marginRight: '4px' }}>💬 Remark:</span>
                            <span style={{ fontWeight: 500 }}>{remark}</span>
                          </div>
                        );
                      })()}
                      {/* Contacted info */}
                      {lead.first_contacted_at ? (
                        <div style={{ fontSize: 10.5, color: '#059669', marginTop: 2 }}>
                          Contacted: {formatDateTime(lead.first_contacted_at)}
                        </div>
                      ) : (
                        lead.assigned_to?.id === userId && (
                          <button
                            style={{ fontSize: 10.5, padding: '2px 7px', marginTop: 3, border: '1px solid #10B981', color: '#10B981', background: 'transparent', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => handleMarkContacted(lead.id)}
                          >
                            Mark Contacted
                          </button>
                        )
                      )}
                    </td>

                    {/* Phone */}
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12, verticalAlign: 'top' }}>
                      <a href={`tel:${lead.phone}`} style={{ color: 'var(--crm-text-primary)', textDecoration: 'none' }}>{lead.phone}</a>
                    </td>

                    {/* Source / Campaign */}
                    <td style={{ verticalAlign: 'top' }}>
                      {lead.source && (
                        <div style={{ fontSize: 12, background: 'var(--crm-bg)', padding: '1px 7px', borderRadius: 4, display: 'inline-block', marginBottom: 3 }}>{lead.source}</div>
                      )}
                      {lead.campaign && (
                        <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>{lead.campaign}</div>
                      )}
                      {!lead.source && !lead.campaign && <span style={{ color: 'var(--crm-text-muted)' }}>Direct</span>}
                    </td>

                    {/* Location */}
                    <td style={{ verticalAlign: 'top' }}>
                      <div style={{ fontSize: 12 }}>
                        {lead.city || lead.country
                          ? `${lead.city || ''}${lead.city && lead.country ? ', ' : ''}${lead.country || ''}`
                          : <span style={{ color: 'var(--crm-text-muted)' }}>—</span>}
                      </div>
                      {lead.industry && (
                        <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginTop: 1 }}>{lead.industry}</div>
                      )}
                    </td>

                    {/* Branch */}
                    <td style={{ verticalAlign: 'top' }}>
                      {(() => {
                        const branch = branches.find((b: any) => b.id === lead.branch_id);
                        return (
                          <span className="crm-status-badge" style={{ background: '#F1F5F9', color: '#334155', fontWeight: 600, fontSize: 11 }}>
                            🏢 {branch ? branch.name : 'Main Branch/Thane'}
                          </span>
                        );
                      })()}
                    </td>

                    {/* Car Interest */}
                    <td style={{ verticalAlign: 'top', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                      {lead.interested_car || <span style={{ color: 'var(--crm-text-muted)' }}>—</span>}
                    </td>

                    {/* Tags */}
                    <td style={{ verticalAlign: 'top' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {(lead.tags || []).map((tag: string) => (
                          <span key={tag} style={{ fontSize: 10.5, background: 'var(--crm-primary-light)', color: 'var(--crm-primary)', padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>

                    {/* Follow-up date & time */}
                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                      {lead.follow_up_date ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            fontSize: '12px',
                            fontWeight: 700,
                            color: overdue ? '#B91C1C' : dueToday ? '#92400E' : '#1E293B'
                          }}>
                            <span>📅 {formatFollowUpDateTime(lead.follow_up_date).datePart}</span>
                            <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748B' }}>
                              ⏰ {formatFollowUpDateTime(lead.follow_up_date).timePart}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {overdue ? (
                              <span style={{ fontSize: '9.5px', padding: '1px 6px', background: '#FEE2E2', color: '#B91C1C', border: '1px solid #FCA5A5', borderRadius: '4px', fontWeight: 800, letterSpacing: '0.3px' }}>
                                🔴 OVERDUE
                              </span>
                            ) : dueToday ? (
                              <span style={{ fontSize: '10px', padding: '2px 7px', background: '#FEF3C7', color: '#92400E', border: '1.5px solid #D97706', borderRadius: '4px', fontWeight: 800, letterSpacing: '0.3px', boxShadow: '0 1px 3px rgba(217,119,6,0.2)' }}>
                                🔔 AAJ KA SCHEDULE
                              </span>
                            ) : isFollowUpTomorrow(lead) ? (
                              <span style={{ fontSize: '9.5px', padding: '1px 6px', background: '#E0F2FE', color: '#0369A1', borderRadius: '4px', fontWeight: 800, letterSpacing: '0.3px' }}>
                                ☀️ TOMORROW
                              </span>
                            ) : (
                              <span style={{ fontSize: '9.5px', padding: '1px 6px', background: '#F1F5F9', color: '#475569', borderRadius: '4px', fontWeight: 700 }}>
                                🟢 SCHEDULED
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => openFollowUpModal(lead)}
                              style={{
                                background: '#F1F5F9',
                                border: '1px solid #CBD5E1',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '10.5px',
                                color: '#334155',
                                padding: '1px 5px',
                                fontWeight: 600,
                                lineHeight: 1
                              }}
                              title="Reschedule / Edit Date & Time"
                            >
                              ✏️ Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openFollowUpModal(lead)}
                          style={{
                            background: '#F8FAFC',
                            border: '1px dashed #94A3B8',
                            borderRadius: '6px',
                            color: '#64748B',
                            fontSize: '11.5px',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Schedule follow-up date and time"
                        >
                          <span>📅</span>
                          <span>+ Schedule</span>
                        </button>
                      )}
                    </td>

                    {/* Status */}
                    <td style={{ verticalAlign: 'top' }}>
                      {lead.status === 'Converted' ? (
                        <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: '#D1FAE5', color: '#065F46', fontWeight: 700, display: 'inline-block' }}>
                          ✓ Converted
                        </span>
                      ) : (
                        <select
                          className="crm-status-select"
                          value={lead.status}
                          onChange={e => handleStatusChange(lead, e.target.value)}
                          style={{
                            fontSize: 12,
                            ...getStatusSelectStyle(lead.status)
                          }}
                        >
                          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                          {!STATUS_OPTIONS.includes(lead.status) && lead.status && (
                            <option value={lead.status}>{lead.status}</option>
                          )}
                        </select>
                      )}
                    </td>

                    {/* Assigned To (admin only) */}
                    {isAdmin && (
                      <td style={{ verticalAlign: 'top' }}>
                        {overrideLeadId === lead.id ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <select
                              className="crm-status-select"
                              value={lead.assigned_to?.id || ''}
                              onChange={async (e) => {
                                await handleReassign(lead.id, e.target.value);
                                setOverrideLeadId(null);
                              }}
                              style={{ maxWidth: 130, fontSize: 12 }}
                              autoFocus
                              onBlur={() => setOverrideLeadId(null)}
                            >
                              <option value="">Unassigned</option>
                              {salesUsers.map(u => (
                                <option key={u.id} value={u.id}>
                                  {u.name}
                                </option>
                              ))}
                              {lead.assigned_to?.id && !salesUsers.some(u => u.id === lead.assigned_to?.id) && (
                                <option value={lead.assigned_to.id} disabled>
                                  {lead.assigned_to.name} (Non-Sales)
                                </option>
                              )}
                            </select>
                            <button
                              type="button"
                              onClick={() => setOverrideLeadId(null)}
                              style={{ background: 'none', border: 'none', color: '#94A3B8', fontSize: 10, cursor: 'pointer', textDecoration: 'underline', alignSelf: 'flex-start', padding: 0 }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 500, color: lead.assigned_to ? 'var(--crm-text-primary)' : 'var(--crm-text-muted)' }}>
                                {lead.assigned_to?.name || 'Queued'}
                              </span>
                              <button
                                type="button"
                                onClick={() => setOverrideLeadId(lead.id)}
                                style={{
                                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                  color: 'var(--crm-primary)', fontSize: 10.5, fontWeight: 600, textDecoration: 'underline'
                                }}
                                title="Admin Manual Override"
                              >
                                Override
                              </button>
                            </div>
                            {lead.assigned_at && (
                              <div style={{ fontSize: 10, color: 'var(--crm-text-muted)', marginTop: 2 }}>
                                Assigned {timeAgo(lead.assigned_at)}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    )}

                    {/* Actions */}
                    <td style={{ verticalAlign: 'top' }}>
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        <button
                          style={{ fontSize: 10.5, padding: '2px 8px', fontWeight: 600, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'var(--crm-primary)', color: 'white', whiteSpace: 'nowrap' }}
                          onClick={() => setSelectedLead(lead)}
                          title="View timeline"
                        >
                          Activity
                        </button>
                        {(lead.status === 'Booked' || lead.status === 'Converted') ? (
                          <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: 'rgba(16, 185, 129, 0.15)', color: '#065F46', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            ✓ Converted
                          </span>
                        ) : (
                          <button
                            style={{ fontSize: 10.5, padding: '2px 8px', fontWeight: 600, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'var(--crm-secondary)', color: 'white', whiteSpace: 'nowrap' }}
                            onClick={() => handleConvertLead(lead)}
                          >
                            Convert
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            style={{ fontSize: 10.5, padding: '2px 7px', fontWeight: 600, border: 'none', borderRadius: 4, cursor: 'pointer', background: '#FEE2E2', color: '#B91C1C' }}
                            onClick={() => handleDeleteLead(lead.id, lead.customer_name)}
                            title="Delete lead"
                          >
                            Del
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Table footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderTop: '1px solid var(--crm-border-light)', background: 'var(--crm-bg)', fontSize: 12, color: 'var(--crm-text-muted)', flexWrap: 'wrap', gap: 12 }}>
            <span>
              Showing <strong>{startIndex}-{endIndex}</strong> of <strong>{totalLeadsCount}</strong> leads
              {hasActiveFilters && ' (filtered)'}
            </span>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                  style={{
                    padding: '3px 8px',
                    border: '1px solid var(--crm-border)',
                    borderRadius: 4,
                    background: 'white',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: 'var(--crm-text-secondary)',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={e => { if (currentPage !== 1) e.currentTarget.style.borderColor = 'var(--crm-primary)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--crm-border)'; }}
                >
                  «
                </button>
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  style={{
                    padding: '3px 8px',
                    border: '1px solid var(--crm-border)',
                    borderRadius: 4,
                    background: 'white',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: 'var(--crm-text-secondary)',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={e => { if (currentPage !== 1) e.currentTarget.style.borderColor = 'var(--crm-primary)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--crm-border)'; }}
                >
                  ‹
                </button>
                {/* Page Number Buttons */}
                {(() => {
                  const pages = [];
                  const maxButtons = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
                  let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                  
                  if (endPage - startPage + 1 < maxButtons) {
                    startPage = Math.max(1, endPage - maxButtons + 1);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        type="button"
                        onClick={() => setCurrentPage(i)}
                        style={{
                          padding: '3px 8px',
                          minWidth: '26px',
                          border: '1px solid',
                          borderColor: currentPage === i ? 'var(--crm-primary)' : 'var(--crm-border)',
                          borderRadius: 4,
                          background: currentPage === i ? 'var(--crm-primary)' : 'white',
                          color: currentPage === i ? 'white' : 'var(--crm-text-secondary)',
                          cursor: 'pointer',
                          fontSize: 11.5,
                          fontWeight: 600,
                          transition: 'all 0.15s'
                        }}
                        onMouseEnter={e => { if (currentPage !== i) e.currentTarget.style.borderColor = 'var(--crm-primary)'; }}
                        onMouseLeave={e => { if (currentPage !== i) e.currentTarget.style.borderColor = 'var(--crm-border)'; }}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}
                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  style={{
                    padding: '3px 8px',
                    border: '1px solid var(--crm-border)',
                    borderRadius: 4,
                    background: 'white',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: 'var(--crm-text-secondary)',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={e => { if (currentPage !== totalPages) e.currentTarget.style.borderColor = 'var(--crm-primary)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--crm-border)'; }}
                >
                  ›
                </button>
                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                  style={{
                    padding: '3px 8px',
                    border: '1px solid var(--crm-border)',
                    borderRadius: 4,
                    background: 'white',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: 'var(--crm-text-secondary)',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={e => { if (currentPage !== totalPages) e.currentTarget.style.borderColor = 'var(--crm-primary)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--crm-border)'; }}
                >
                  »
                </button>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>Sort:</span>
              <button onClick={() => handleSort('created_at')} style={{ fontSize: 11.5, padding: '2px 8px', border: '1px solid var(--crm-border)', borderRadius: 4, cursor: 'pointer', background: sortBy === 'created_at' ? 'var(--crm-primary)' : 'white', color: sortBy === 'created_at' ? 'white' : 'var(--crm-text-muted)', fontWeight: 600 }}>Date {sortBy === 'created_at' ? (sortDir === 'desc' ? '▼' : '▲') : ''}</button>
              <button onClick={() => handleSort('follow_up_date')} style={{ fontSize: 11.5, padding: '2px 8px', border: '1px solid var(--crm-border)', borderRadius: 4, cursor: 'pointer', background: sortBy === 'follow_up_date' ? 'var(--crm-primary)' : 'white', color: sortBy === 'follow_up_date' ? 'white' : 'var(--crm-text-muted)', fontWeight: 600 }}>Follow-up {sortBy === 'follow_up_date' ? (sortDir === 'desc' ? '▼' : '▲') : ''}</button>
              <button onClick={() => handleSort('status')} style={{ fontSize: 11.5, padding: '2px 8px', border: '1px solid var(--crm-border)', borderRadius: 4, cursor: 'pointer', background: sortBy === 'status' ? 'var(--crm-primary)' : 'white', color: sortBy === 'status' ? 'white' : 'var(--crm-text-muted)', fontWeight: 600 }}>Status {sortBy === 'status' ? (sortDir === 'desc' ? '▼' : '▲') : ''}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Follow-up Scheduling Modal ── */}
      {schedulingLead && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', maxWidth: '480px', width: '100%',
            padding: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.25)', position: 'relative'
          }}>
            <button
              type="button"
              onClick={() => setSchedulingLead(null)}
              style={{
                position: 'absolute', top: '16px', right: '16px', background: '#F1F5F9',
                border: 'none', borderRadius: '50%', width: '30px', height: '30px',
                cursor: 'pointer', fontSize: '15px', color: '#64748B', display: 'flex',
                alignItems: 'center', justifyContent: 'center'
              }}
            >
              ✕
            </button>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                <h3 style={{ margin: 0, fontSize: '17px', color: '#1E293B', fontWeight: 800, fontFamily: 'Inter, sans-serif' }}>
                  🗓️ Schedule Follow-up & Add Remark
                </h3>
              </div>
              <p style={{ margin: '4px 0 0 0', fontSize: '12.5px', color: '#64748B' }}>
                Customer: <strong>{schedulingLead.customer_name}</strong> ({schedulingLead.phone})
              </p>
              <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Selected Status:</span>
                <select
                  value={schedulingTargetStatus}
                  onChange={e => {
                    const nextSt = e.target.value;
                    setSchedulingTargetStatus(nextSt);
                    if (nextSt === 'Call not received / Ringing') setPresetIn2Hours();
                    else if (nextSt === 'Interested' || nextSt === 'Hot') setPresetTomorrowMorning();
                  }}
                  style={{
                    ...getStatusSelectStyle(schedulingTargetStatus),
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    border: '1.5px solid',
                    cursor: 'pointer'
                  }}
                >
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* Quick Presets */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '8px' }}>
                <span>⚡ Quick Shortcuts (1-Click Select)</span>
                {selectedPreset !== 'custom' && selectedPreset !== 'none' && (
                  <span style={{ fontSize: '11px', color: '#2563EB', fontWeight: 600 }}>Preset active</span>
                )}
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                {/* Preset 0: In 2 Hours */}
                <button
                  type="button"
                  onClick={setPresetIn2Hours}
                  style={{
                    padding: '9px 11px',
                    background: selectedPreset === 'in_2_hours' ? 'linear-gradient(135deg, #EFF6FF, #DBEAFE)' : '#FFFFFF',
                    border: selectedPreset === 'in_2_hours' ? '2px solid #2563EB' : '1.5px solid #CBD5E1',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: selectedPreset === 'in_2_hours' ? '#1D4ED8' : '#334155',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: selectedPreset === 'in_2_hours' ? '0 0 0 3px rgba(37, 99, 235, 0.2)' : '0 1px 2px rgba(0,0,0,0.04)',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <span style={{ fontSize: '16px' }}>⏱️</span>
                    <div>
                      <div style={{ fontWeight: 700 }}>In 2 Hours</div>
                      <div style={{ fontSize: '10.5px', color: selectedPreset === 'in_2_hours' ? '#2563EB' : '#64748B', fontWeight: 600 }}>Call again</div>
                    </div>
                  </div>
                  {selectedPreset === 'in_2_hours' && (
                    <span style={{ background: '#2563EB', color: 'white', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 800 }}>✓</span>
                  )}
                </button>

                {/* Preset 1: Parso (2 Days) - 9 AM */}
                <button
                  type="button"
                  onClick={setPresetDayAfterMorning}
                  style={{
                    padding: '9px 11px',
                    background: selectedPreset === 'parso_morning' ? 'linear-gradient(135deg, #EFF6FF, #DBEAFE)' : '#FFFFFF',
                    border: selectedPreset === 'parso_morning' ? '2px solid #2563EB' : '1.5px solid #CBD5E1',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: selectedPreset === 'parso_morning' ? '#1D4ED8' : '#334155',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: selectedPreset === 'parso_morning' ? '0 0 0 3px rgba(37, 99, 235, 0.2)' : '0 1px 2px rgba(0,0,0,0.04)',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <span style={{ fontSize: '16px' }}>🗓️</span>
                    <div>
                      <div style={{ fontWeight: 700 }}>Parso (2 Days)</div>
                      <div style={{ fontSize: '10.5px', color: selectedPreset === 'parso_morning' ? '#2563EB' : '#64748B', fontWeight: 600 }}>09:00 AM</div>
                    </div>
                  </div>
                  {selectedPreset === 'parso_morning' && (
                    <span style={{ background: '#2563EB', color: 'white', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 800 }}>✓</span>
                  )}
                </button>

                {/* Preset 2: Tomorrow - 10 AM */}
                <button
                  type="button"
                  onClick={setPresetTomorrowMorning}
                  style={{
                    padding: '9px 11px',
                    background: selectedPreset === 'tomorrow_morning' ? 'linear-gradient(135deg, #EFF6FF, #DBEAFE)' : '#FFFFFF',
                    border: selectedPreset === 'tomorrow_morning' ? '2px solid #2563EB' : '1.5px solid #CBD5E1',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: selectedPreset === 'tomorrow_morning' ? '#1D4ED8' : '#334155',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: selectedPreset === 'tomorrow_morning' ? '0 0 0 3px rgba(37, 99, 235, 0.2)' : '0 1px 2px rgba(0,0,0,0.04)',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <span style={{ fontSize: '16px' }}>☀️</span>
                    <div>
                      <div style={{ fontWeight: 700 }}>Tomorrow</div>
                      <div style={{ fontSize: '10.5px', color: selectedPreset === 'tomorrow_morning' ? '#2563EB' : '#64748B', fontWeight: 600 }}>10:00 AM</div>
                    </div>
                  </div>
                  {selectedPreset === 'tomorrow_morning' && (
                    <span style={{ background: '#2563EB', color: 'white', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 800 }}>✓</span>
                  )}
                </button>

                {/* Preset 3: Today Evening - 5 PM */}
                <button
                  type="button"
                  onClick={setPresetTodayEvening}
                  style={{
                    padding: '9px 11px',
                    background: selectedPreset === 'today_evening' ? 'linear-gradient(135deg, #EFF6FF, #DBEAFE)' : '#FFFFFF',
                    border: selectedPreset === 'today_evening' ? '2px solid #2563EB' : '1.5px solid #CBD5E1',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: selectedPreset === 'today_evening' ? '#1D4ED8' : '#334155',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: selectedPreset === 'today_evening' ? '0 0 0 3px rgba(37, 99, 235, 0.2)' : '0 1px 2px rgba(0,0,0,0.04)',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <span style={{ fontSize: '16px' }}>🌅</span>
                    <div>
                      <div style={{ fontWeight: 700 }}>Today Evening</div>
                      <div style={{ fontSize: '10.5px', color: selectedPreset === 'today_evening' ? '#2563EB' : '#64748B', fontWeight: 600 }}>05:00 PM</div>
                    </div>
                  </div>
                  {selectedPreset === 'today_evening' && (
                    <span style={{ background: '#2563EB', color: 'white', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 800 }}>✓</span>
                  )}
                </button>
              </div>

              {/* Live Preview Bar */}
              {followUpDateInput ? (
                <div style={{
                  background: '#F0FDF4',
                  border: '1.5px solid #86EFAC',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  marginTop: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12.5px',
                  color: '#166534',
                  boxShadow: '0 1px 3px rgba(22, 101, 52, 0.08)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '14px' }}>⏰</span>
                    <span>
                      Selected: <strong>{new Date(followUpDateInput).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}, {new Date(followUpDateInput).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</strong>
                    </span>
                  </div>
                  <span style={{
                    background: selectedPreset !== 'custom' ? '#DCFCE7' : '#EFF6FF',
                    color: selectedPreset !== 'custom' ? '#15803D' : '#1D4ED8',
                    border: `1px solid ${selectedPreset !== 'custom' ? '#BBF7D0' : '#BFDBFE'}`,
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 700
                  }}>
                    {selectedPreset === 'in_2_hours' ? 'In 2 Hours' : selectedPreset === 'parso_morning' ? 'Parso 9 AM' : selectedPreset === 'tomorrow_morning' ? 'Tomorrow 10 AM' : selectedPreset === 'today_evening' ? 'Today 5 PM' : selectedPreset === 'next_week' ? 'Next Mon 10 AM' : 'Custom'}
                  </span>
                </div>
              ) : (
                <div style={{
                  background: '#F8FAFC',
                  border: '1px dashed #CBD5E1',
                  borderRadius: '8px',
                  padding: '7px 12px',
                  marginTop: '10px',
                  fontSize: '12px',
                  color: '#64748B',
                  textAlign: 'center'
                }}>
                  No follow-up date scheduled (Remark-only mode)
                </div>
              )}
            </div>

            <form onSubmit={handleSaveFollowUp} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                  <span>Custom Date & Time Picker</span>
                  {selectedPreset === 'custom' && (
                    <span style={{ fontSize: '11px', color: '#2563EB', fontWeight: 700 }}>Custom Time Mode</span>
                  )}
                </label>
                <input
                  type="datetime-local"
                  value={followUpDateInput}
                  onChange={e => {
                    setFollowUpDateInput(e.target.value);
                    setSelectedPreset('custom');
                  }}
                  style={{
                    width: '100%', padding: '10px 12px',
                    border: selectedPreset === 'custom' ? '2px solid #2563EB' : '1.5px solid #CBD5E1',
                    background: selectedPreset === 'custom' ? '#F0F9FF' : 'white',
                    borderRadius: '8px', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box',
                    fontWeight: 600, color: '#1E293B',
                    boxShadow: selectedPreset === 'custom' ? '0 0 0 3px rgba(37, 99, 235, 0.15)' : 'none',
                    transition: 'all 0.15s ease'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                  💬 Remark / Note / Message
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Call not received / customer asked to call back / interested in car details..."
                  value={followUpNoteInput}
                  onChange={e => setFollowUpNoteInput(e.target.value)}
                  style={{
                    width: '100%', padding: '9px 12px', border: '1.5px solid #CBD5E1',
                    borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                    fontFamily: 'inherit', resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: '8px', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  {schedulingLead.follow_up_date && (
                    <button
                      type="button"
                      onClick={handleClearFollowUp}
                      style={{
                        padding: '9px 14px', background: '#FEE2E2', color: '#B91C1C',
                        border: '1px solid #FCA5A5', borderRadius: '8px', fontWeight: 600, fontSize: '12.5px', cursor: 'pointer'
                      }}
                    >
                      Clear Follow-up
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => setSchedulingLead(null)}
                    style={{
                      padding: '9px 16px', background: '#F1F5F9', color: '#475569',
                      border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '13px', cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  {(schedulingTargetStatus === 'Not Interested' || schedulingTargetStatus === 'Virus' || !followUpDateInput) && (
                    <button
                      type="button"
                      onClick={() => handleSaveFollowUp(undefined, true)}
                      style={{
                        padding: '9px 14px', background: '#F1F5F9', color: '#1E293B',
                        border: '1.5px solid #CBD5E1', borderRadius: '8px', fontWeight: 600, fontSize: '12.5px', cursor: 'pointer'
                      }}
                      title="Save remark and status without scheduling follow-up date"
                    >
                      Save Without Schedule
                    </button>
                  )}
                  <button
                    type="submit"
                    style={{
                      padding: '9px 20px', background: 'var(--crm-primary)', color: 'white',
                      border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '13px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '6px'
                    }}
                  >
                    <span>💾</span>
                    <span>Save Schedule</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Lead Name Modal */}
      {editingNameLead && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', maxWidth: '440px', width: '100%',
            padding: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.25)', position: 'relative'
          }}>
            <button
              type="button"
              onClick={() => setEditingNameLead(null)}
              style={{
                position: 'absolute', top: '16px', right: '16px', background: '#F1F5F9',
                border: 'none', borderRadius: '50%', width: '30px', height: '30px',
                cursor: 'pointer', fontSize: '15px', color: '#64748B', display: 'flex',
                alignItems: 'center', justifyContent: 'center'
              }}
            >
              ✕
            </button>

            <div style={{ marginBottom: '18px' }}>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '17px', color: '#1E293B', fontWeight: 800, fontFamily: 'Inter, sans-serif' }}>
                ✏️ Edit Lead / Customer Naming
              </h3>
              <p style={{ margin: 0, fontSize: '12.5px', color: '#64748B' }}>
                Update person name and add optional Tours & Travels name
              </p>
            </div>

            <form onSubmit={handleSaveLeadName} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                  Customer / Person Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Rohit or Deepak Sharma"
                  value={editingNameLead.mainName}
                  onChange={e => setEditingNameLead({ ...editingNameLead, mainName: e.target.value })}
                  style={{
                    width: '100%', padding: '9px 12px', border: '1.5px solid #CBD5E1',
                    borderRadius: '8px', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                  Tours & Travels / Business Name (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. G A Tours or Royal Travels"
                  value={editingNameLead.travelsName}
                  onChange={e => setEditingNameLead({ ...editingNameLead, travelsName: e.target.value })}
                  style={{
                    width: '100%', padding: '9px 12px', border: '1.5px solid #CBD5E1',
                    borderRadius: '8px', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box'
                  }}
                />
                <div style={{ fontSize: '11.5px', color: '#0369A1', background: '#F0F9FF', border: '1px solid #BAE6FD', padding: '8px 10px', borderRadius: '6px', marginTop: '8px', lineHeight: '1.4' }}>
                  💡 <strong>Display Note:</strong> Tours & Travels name will be displayed in small font next to the customer name across all screens and converted workflow.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: '8px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setEditingNameLead(null)}
                  style={{
                    padding: '9px 16px', background: '#F1F5F9', color: '#475569',
                    border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '13px', cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '9px 20px', background: '#0284C7', color: 'white',
                    border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '13px', cursor: 'pointer'
                  }}
                >
                  {saving ? 'Saving...' : 'Save Naming'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsManagement;
